import { Maybe, Price, PriceList, StockStatus } from '../../types';
import { PriceDisplay, PriceDisplayEnum, SortOptionEnum } from './types';

enum SortDirection {
  Ascending,
  Descending,
}

interface IPropertyResolver<T> {
  resolver: (offer: Price) => T;
  sortDirection: SortDirection;
}

const sortByPriceProp = (priceDisplay: PriceDisplay) => {
  const resolver =
    priceDisplay === PriceDisplayEnum.PRICE_INCLUDES_SHIPPING
      ? (offer: Price) => {
          return offer?.price?.inclShipping || offer?.price?.exclShipping;
        }
      : (offer: Price) => offer.price && offer.price.exclShipping;

  return { resolver, sortDirection: SortDirection.Ascending };
};

export const sortByPrice = (priceDisplay: PriceDisplay, prices: Price[]) => {
  const prop = sortByPriceProp(priceDisplay);

  return genericSort(prices, prop);
};

export const sortByWeight = (prices: Price[]) => {
  return genericSort(prices, {
    sortDirection: SortDirection.Descending,
    resolver: offer => {
      return offer?.weight || 0;
    },
  });
};

const getStockStatusAsNumber = (
  stockStatus: StockStatus | undefined | null,
  featured: boolean | undefined | null
) => {
  if (!featured) {
    return 0;
  }

  switch (stockStatus) {
    case 'in_stock':
      return 5;
    case 'in_stock_preorder':
      return 4;
    case 'incoming':
      return 3;
    case 'not_in_stock':
      return 1;
    case 'unknown':
    default:
      return 0;
  }
};

export const sortByStockStatus = (
  priceDisplay: PriceDisplay,
  prices: Price[]
) => {
  const primary = {
    resolver: (offer: Price) =>
      getStockStatusAsNumber(offer?.stock?.status, offer?.store?.featured),
    sortDirection: SortDirection.Descending,
  };
  const secondary = sortByPriceProp(priceDisplay);

  return genericSort(prices, primary, secondary);
};

const genericSort = <T, TU>(
  prices: Price[],
  primaryProp: IPropertyResolver<T>,
  secondaryProp?: IPropertyResolver<TU>
) => {
  return [...prices].sort((a, b) => {
    const firstSort = orderItem(a, b, primaryProp);
    if (firstSort !== 0) {
      return firstSort;
    }

    if (secondaryProp) {
      return orderItem(a, b, secondaryProp);
    }

    return 0;
  });
};

const orderItem = <T>(a: Price, b: Price, prop: IPropertyResolver<T>) => {
  const aProp = prop.resolver(a);
  const bProp = prop.resolver(b);

  if (aProp < bProp) {
    return prop.sortDirection === SortDirection.Descending ? 1 : -1;
  }

  if (aProp > bProp) {
    return prop.sortDirection === SortDirection.Descending ? -1 : 1;
  }

  return 0;
};

export const getSortedPrices = (
  priceDisplay: PriceDisplay,
  sorting: SortOptionEnum,
  prices?: Maybe<PriceList>
): Price[] | undefined | null => {
  const allPrices = prices?.nodes?.filter(x => x) as Price[] | null | undefined;
  if (allPrices?.length) {
    switch (sorting) {
      case SortOptionEnum.PRICE:
        return sortByPrice(priceDisplay, allPrices);
      case SortOptionEnum.RECOMMENDED:
        return sortByWeight(allPrices);
      case SortOptionEnum.PRICEANDSTOCKSTATUS:
        return sortByStockStatus(priceDisplay, allPrices);
      default:
        return allPrices;
    }
  }

  return allPrices;
};
